import { useMemo, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types/customer";
import { IssueCard } from "@/components/letters/issue-card";
import { NoticePdfButton } from "@/components/notices/notice-pdf-button";
import { RowsSkeleton } from "@/components/properties/rows-skeleton";
import { LettersTabs } from "@/components/shell/letters-tabs";
import {
  Card,
  EmptyState,
  FilterBar,
  FormField,
  LetterRow,
  LoadError,
  PageTitle,
  Pagination,
  RowList,
  Select,
} from "@/components/ui";
import { useAccountDevices } from "@/hooks/use-account-devices";
import { useActiveAccountId, useActiveMembership } from "@/hooks/use-customer-context";
import { type NoticeListParams, useCustomerNotices } from "@/hooks/use-customer-notices";
import { useCustomerViolations } from "@/hooks/use-customer-violations";
import { useForeignPropertyReset } from "@/hooks/use-foreign-property-reset";
import { useLetterYears } from "@/hooks/use-letter-years";
import { letterLink } from "@/lib/destinations";
import { deviceLabel } from "@/lib/device-label";
import { openIssueSummary } from "@/lib/issue-list";
import { letterFileName } from "@/lib/letter-file-name";
import { LETTERS_PAGE_SIZE, validateLettersSearch } from "@/lib/letters-search";
import { present } from "@/lib/present";

type CustomerInstallation = components["schemas"]["CustomerInstallation"];
type CustomerViolation = components["schemas"]["CustomerViolation"];

export const Route = createFileRoute("/_customer/letters/")({
  validateSearch: validateLettersSearch,
  component: LettersPage,
});

const NO_VIOLATIONS: readonly CustomerViolation[] = [];

function nameOf(installation: CustomerInstallation | undefined): string | undefined {
  if (!installation) return undefined;
  const name = deviceLabel({
    sizeLabel: installation.size_label,
    assemblyType: installation.assembly_type,
    manufacturerLabel: installation.manufacturer_label,
    modelLabel: installation.model_label,
  });
  return name || undefined;
}

/**
 * Letters: the letters the active account's utility sent, newest first, filtered
 * by property and year and paged on the server, every control in the URL. Open
 * compliance issues are counted on their tab and, above the letters, named on
 * a red card with the first deadline.
 */
function LettersPage() {
  const { t } = useTranslation("customer");
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { property, year, page } = search;
  const accountId = useActiveAccountId();
  const active = useActiveMembership();
  const timezone = active?.org_timezone;
  const org = active?.org_name ?? "";
  const devices = useAccountDevices();
  const { installations, facilities, facilitiesById } = devices;
  useForeignPropertyReset(property, devices.isPending || devices.isError ? undefined : facilities);
  const years = useLetterYears(accountId);
  const violationsRead = useCustomerViolations(accountId ? { account_id: accountId } : {});
  const issues = openIssueSummary(violationsRead.data?.items ?? NO_VIOLATIONS);

  // One params object per URL and account, so the read's key and the cache
  // entry it names hold still across renders. Every filter, the sort and the
  // page reach the server; nothing narrows a page here.
  const params = useMemo<NoticeListParams>(
    () => ({
      ...(accountId ? { account_id: accountId } : {}),
      ...(property ? { facility_id: property } : {}),
      ...(year ? { year } : {}),
      sort_by: "generated_at",
      sort_dir: "desc",
      limit: LETTERS_PAGE_SIZE,
      offset: ((page ?? 1) - 1) * LETTERS_PAGE_SIZE,
    }),
    [accountId, property, year, page],
  );
  const read = useCustomerNotices(params);

  const installationsById = useMemo(
    () => new Map(installations.map((installation) => [installation.id, installation])),
    [installations],
  );
  // The URL's year stays offered while the options are still read, or when it
  // lies outside them, so the menu never shows "All years" over a set filter.
  const yearOptions = useMemo(
    () =>
      year === undefined || years.includes(year) ? years : [...years, year].sort((a, b) => b - a),
    [years, year],
  );
  const propertyOptions = useMemo(
    () =>
      [...facilities]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((facility) => ({ value: facility.id, label: facility.name })),
    [facilities],
  );

  // Every write passes through the validator, so the URL holds what the page
  // reads back: an empty choice and the first page are the param's absence. A
  // filter change names no page, so the page drops.
  const onSearch = (next: Record<string, unknown>) =>
    void navigate({ to: "/letters", search: validateLettersSearch(next) });

  const filtered = property !== undefined || year !== undefined;

  let body: ReactNode;
  if (read.isError) {
    body = (
      <LoadError
        title={t("letters.error")}
        body={t("loadError.body")}
        onRetry={() => void read.refetch()}
      />
    );
  } else if (read.isPending) {
    body = <RowsSkeleton />;
  } else if (read.data.items.length > 0) {
    const total = read.data.total_count;
    body = (
      <div>
        <RowList>
          {read.data.items.map((notice) => {
            const title = present(notice.subject) ?? t("letterRow.untitled", { org });
            return (
              <LetterRow
                key={notice.id}
                notice={notice}
                link={letterLink(notice.id)}
                orgName={org}
                timezone={timezone}
                contextLabel={
                  facilitiesById.get(notice.facility_id ?? "")?.name ??
                  nameOf(installationsById.get(notice.device_installation_id ?? ""))
                }
                trailing={
                  <NoticePdfButton
                    noticeId={notice.id}
                    hasPdf={notice.has_pdf ?? false}
                    fileName={letterFileName(title, notice.generated_at, timezone)}
                  />
                }
              />
            );
          })}
        </RowList>
        {total > LETTERS_PAGE_SIZE && (
          <Pagination
            currentPage={page ?? 1}
            totalPages={Math.ceil(total / LETTERS_PAGE_SIZE)}
            totalItems={total}
            pageSize={LETTERS_PAGE_SIZE}
            onPageChange={(next) => onSearch({ property, year, page: next })}
          />
        )}
      </div>
    );
  } else if (filtered || page !== undefined) {
    // A page past the last one reads as no match too, and Reset returns to the first.
    body = (
      <Card>
        <EmptyState
          variant="inline"
          title={t("letters.noMatch")}
          actionLabel={t("filters.reset")}
          onAction={() => onSearch({})}
        />
      </Card>
    );
  } else {
    body = (
      <Card>
        <p className="text-sm text-body">{t("letters.empty", { org })}</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5 lg:gap-6">
      <PageTitle title={t("letters.title")} sub={t("letters.sub", { org })}>
        <LettersTabs value="letters" openIssues={issues.count} />
      </PageTitle>
      <div className="flex flex-col gap-4">
        {issues.count > 0 && <IssueCard count={issues.count} earliestDue={issues.earliestDue} />}
        <FilterBar onReset={() => onSearch({})} canReset={filtered}>
          <div className="sm:w-56">
            <FormField label={t("letters.property")}>
              <Select
                value={property ?? ""}
                options={[{ value: "", label: t("letters.propertyAll") }, ...propertyOptions]}
                onChange={(event) => onSearch({ property: event.target.value, year })}
              />
            </FormField>
          </div>
          <div className="sm:w-40">
            <FormField label={t("letters.year")}>
              <Select
                value={year === undefined ? "" : String(year)}
                options={[
                  { value: "", label: t("letters.yearAll") },
                  ...yearOptions.map((option) => ({
                    value: String(option),
                    label: String(option),
                  })),
                ]}
                onChange={(event) => onSearch({ property, year: event.target.value })}
              />
            </FormField>
          </div>
        </FilterBar>
        {body}
      </div>
    </div>
  );
}
